import { useLanguage } from '@/contexts/LanguageContext';

export default function ContactMap() {
  const { t } = useLanguage();

  const infoItems = [
    {
      icon: 'ri-map-pin-2-fill',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      label: t('公司地址', 'Company Address', '会社住所'),
      value: t(
        '429台中市神岡區大富路12號',
        '12, Dafu Rd., Shengang Dist., Taichung City 429, Taiwan',
        '台湾台中市神岡区大富路12号 429'
      ),
      href: 'https://maps.google.com/?q=24.237272,120.702951',
      linkText: t('在 Google Maps 開啟', 'Open in Google Maps', 'Google Mapsで開く'),
    },
    {
      icon: 'ri-phone-fill',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      label: t('聯絡電話', 'Phone', '電話番号'),
      value: '+886 958-471-138',
      href: 'tel:+886958471138',
      linkText: null,
    },
    {
      icon: 'ri-mail-fill',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      label: 'Email',
      value: 'chang.chunghung@polyquantummaterials.com',
      href: 'mailto:chang.chunghung@polyquantummaterials.com',
      linkText: null,
    },
    {
      icon: 'ri-time-fill',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      label: t('回覆時間', 'Response Time', '返信時間'),
      value: t('3 個工作天內回覆', 'Reply within 3 business days', '3営業日以内に返信'),
      href: null,
      linkText: null,
    },
  ];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="mb-8">
          <p className="text-[0.9375rem] font-semibold text-brand-green uppercase tracking-widest mb-2">
            {t('公司位置', 'Our Location', '会社所在地')}
          </p>
          <h2
            className="font-bold text-neutral-dark"
            style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.875rem)', lineHeight: '1.35' }}
          >
            {t('歡迎蒞臨聚量應用材料', 'Welcome to PolyQ Applied Materials', '聚量応用材料へようこそ')}
          </h2>
        </div>

        {/* Map + Info layout */}
        <div className="rounded-2xl overflow-hidden border border-neutral-border bg-white" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Google Map — takes 3/5 width on desktop */}
            <div className="lg:col-span-3 relative h-[460px]">
              <iframe
                title={t('聚量應用材料位置地圖', 'PolyQ Applied Materials Location Map', '聚量応用材料所在地マップ')}
                src="https://maps.google.com/maps?q=429%E5%8F%B0%E4%B8%AD%E5%B8%82%E7%A5%9E%E5%B4%97%E5%8D%80%E5%A4%A7%E5%AF%8C%E8%B7%AF12%E8%99%9F&t=&z=16&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Info panel — takes 2/5 width on desktop */}
            <div className="lg:col-span-2 flex flex-col justify-between p-7 md:p-8 border-t lg:border-t-0 lg:border-l border-neutral-border">
              {/* Company name */}
              <div>
                <div className="flex items-center gap-3 mb-6 pb-5 border-b border-neutral-border">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-green to-brand-blue flex-shrink-0">
                    <i className="ri-leaf-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-neutral-dark text-[1.0625rem] leading-tight">
                      {t('聚量應用材料股份有限公司', 'PolyQ Applied Materials Co., Ltd.', '聚量応用材料株式会社')}
                    </p>
                    <p className="text-neutral-mid text-[0.8125rem] mt-0.5">PolyQ Applied Materials</p>
                  </div>
                </div>

                {/* Info items */}
                <div className="space-y-5">
                  {infoItems.map((item) => (
                    <div key={item.label} className="flex items-start gap-3.5">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${item.iconBg} flex-shrink-0 mt-0.5`}>
                        <i className={`${item.icon} ${item.iconColor} text-[1.0625rem]`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[0.8125rem] font-semibold text-neutral-mid uppercase tracking-wide mb-0.5">
                          {item.label}
                        </p>
                        {item.href ? (
                          <a
                            href={item.href}
                            target={item.href.startsWith('http') ? '_blank' : undefined}
                            rel={item.href.startsWith('http') ? 'noopener noreferrer nofollow' : undefined}
                            className="text-neutral-dark text-[0.9375rem] font-medium hover:text-brand-green transition-colors cursor-pointer break-all leading-snug"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-neutral-dark text-[0.9375rem] font-medium leading-snug">{item.value}</p>
                        )}
                        {item.linkText && item.href && (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="inline-flex items-center gap-1 mt-1.5 text-brand-green text-[0.875rem] font-medium hover:underline cursor-pointer"
                          >
                            <i className="ri-external-link-line text-xs"></i>
                            {item.linkText}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA button */}
              <div className="mt-8 pt-6 border-t border-neutral-border">
                <a
                  href="https://maps.google.com/?q=24.237272,120.702951"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-green text-white text-[0.9375rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-navigation-line text-base"></i>
                  {t('導航至公司', 'Get Directions', 'ナビゲーション')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
